import {useCallback, useEffect, useRef} from 'react';
import {
    useGetBenchVariations,
    useGetExtraVariations,
    useGetProductVariations,
} from 'components/customer/Confirmation/Checkout/helpers/useGetVariations';

const MAX_POLLING_ATTEMPTS = 15;
const POLLINT_INTERVAL = 1000;

export const useCheckProductVariations = () => {
    const timerRef = useRef<number>();
    const {variations: benchVariations, isLoaded} = useGetBenchVariations();
    const {
        variations: productVariations,
        isLoading: isLoadingProductVariations,
    } = useGetProductVariations();
    const {variations: extraVariations, isLoading: isLoadingExtraVariations} =
        useGetExtraVariations();

    const checkDataLoaded = useCallback(() => {
        return (
            isLoaded && !isLoadingProductVariations && !isLoadingExtraVariations
        );
    }, [isLoaded, isLoadingProductVariations, isLoadingExtraVariations]);

    const checkVariations = useCallback(() => {
        return (
            benchVariations.length > 0 ||
            productVariations.length > 0 ||
            extraVariations.length > 0
        );
    }, [benchVariations, productVariations, extraVariations]);

    const checkIfThereAreVariations = useCallback(() => {
        clearInterval(timerRef.current);

        return new Promise<boolean>((resolve, reject) => {
            if (checkDataLoaded()) {
                resolve(checkVariations());
            } else {
                let attempts = 1;
                timerRef.current = window.setInterval(() => {
                    if (attempts >= MAX_POLLING_ATTEMPTS) {
                        clearInterval(timerRef.current);
                        reject(new Error('Timeout waiting for data to load'));
                        return;
                    }

                    if (checkDataLoaded()) {
                        resolve(checkVariations());
                        clearInterval(timerRef.current);
                    } else {
                        attempts += 1;
                    }
                }, POLLINT_INTERVAL);
            }
        });
    }, [checkDataLoaded, checkVariations]);

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearInterval(timerRef.current);
            }
        };
    }, []);

    return {
        isLoading:
            isLoadingProductVariations || isLoadingExtraVariations || !isLoaded,
        checkIfThereAreVariations,
    };
};
